import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, Eye, EyeOff, ClipboardList, X, Save, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const EMPTY_Q = { question_text: "", option_a: "", option_b: "", option_c: "", option_d: "", correct_answer: "A", explanation: "" };

export default function AdminMockTests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", time_limit_minutes: 30, total_marks: 100, published: true, questions: [] });
  const [saving, setSaving] = useState(false);
  const [expandedQ, setExpandedQ] = useState(null);

  const fetchTests = async () => {
    try {
      const { data } = await api.get(`/admin/mock-tests?search=${search}`);
      setTests(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTests(); }, [search]);

  const resetForm = () => {
    setForm({ title: "", description: "", time_limit_minutes: 30, total_marks: 100, published: true, questions: [] });
    setEditing(null);
    setShowForm(false);
    setExpandedQ(null);
  };

  const handleEdit = async (test) => {
    try {
      const { data } = await api.get(`/mock-tests/${test.id}`);
      setForm({
        title: data.title,
        description: data.description || "",
        time_limit_minutes: data.time_limit_minutes || 30,
        total_marks: data.total_marks || 100,
        published: data.published,
        questions: data.questions || [],
      });
      setEditing(test.id);
      setShowForm(true);
    } catch (err) {
      alert("Failed to load test details");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.questions.length === 0) {
      alert("Add at least one question");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/mock-tests/${editing}`, form);
      } else {
        await api.post("/admin/mock-tests", form);
      }
      resetForm();
      fetchTests();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mock test and all its attempts?")) return;
    try {
      await api.delete(`/admin/mock-tests/${id}`);
      fetchTests();
    } catch (err) {
      alert("Failed to delete");
    }
  };

  const togglePublish = async (test) => {
    try {
      await api.put(`/admin/mock-tests/${test.id}`, { published: !test.published });
      fetchTests();
    } catch (err) {
      alert("Failed to update");
    }
  };

  const addQuestion = () => {
    setForm(prev => ({ ...prev, questions: [...prev.questions, { ...EMPTY_Q }] }));
    setExpandedQ(form.questions.length);
  };

  const updateQuestion = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === idx ? { ...q, [field]: value } : q)
    }));
  };

  const removeQuestion = (idx) => {
    setForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
    setExpandedQ(null);
  };

  const inputClass = "w-full bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-mock-tests-page">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Mock Tests</h1>
          <button data-testid="add-mock-test-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0284C7] text-white px-4 py-2 text-sm font-bold flex items-center gap-2 hover:bg-[#0369A1] transition-none">
            <Plus size={16} /> Add Test
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid="search-mock-tests" type="text" placeholder="Search mock tests..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-slate-50 border border-slate-200 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0284C7]" />
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="mock-test-form-modal">
            <div className="bg-white w-full max-w-2xl my-8 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">{editing ? "Edit Mock Test" : "Create Mock Test"}</h2>
                <button onClick={resetForm} className="text-slate-400"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Title *</label>
                    <input data-testid="mt-title" type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Time Limit (minutes)</label>
                    <input data-testid="mt-time" type="number" value={form.time_limit_minutes} onChange={(e) => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || 30 })} className={inputClass} min={1} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Total Marks</label>
                    <input data-testid="mt-marks" type="number" value={form.total_marks} onChange={(e) => setForm({ ...form, total_marks: parseInt(e.target.value) || 100 })} className={inputClass} min={1} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Description</label>
                    <input data-testid="mt-description" type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid="mt-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#0284C7]" />
                  <span className="text-sm font-medium">Published</span>
                </label>

                {/* Questions */}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-slate-900">Questions ({form.questions.length})</h3>
                    <button type="button" data-testid="add-question-btn" onClick={addQuestion} className="text-xs font-bold text-[#0284C7] flex items-center gap-1">
                      <Plus size={14} /> Add Question
                    </button>
                  </div>

                  {form.questions.length === 0 && (
                    <div className="text-center py-6 bg-slate-50 border border-dashed border-slate-200" data-testid="no-questions">
                      <AlertCircle size={24} className="mx-auto text-slate-300 mb-1" />
                      <p className="text-xs text-slate-400">No questions added yet</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    {form.questions.map((q, idx) => (
                      <div key={idx} data-testid={`question-${idx}`} className="border border-slate-200 bg-white">
                        <button type="button" onClick={() => setExpandedQ(expandedQ === idx ? null : idx)} className="w-full px-3 py-2.5 flex items-center justify-between text-left">
                          <span className="text-xs font-bold text-slate-700">Q{idx + 1}. {q.question_text || "(empty)"}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5">Ans: {q.correct_answer}</span>
                            {expandedQ === idx ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                          </div>
                        </button>
                        {expandedQ === idx && (
                          <div className="px-3 pb-3 space-y-2 border-t border-slate-100">
                            <div className="mt-2">
                              <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Question Text *</label>
                              <textarea data-testid={`q-text-${idx}`} value={q.question_text} onChange={(e) => updateQuestion(idx, "question_text", e.target.value)} className={`${inputClass} h-16 resize-none`} required />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Option A *</label>
                                <input data-testid={`q-a-${idx}`} value={q.option_a} onChange={(e) => updateQuestion(idx, "option_a", e.target.value)} className={inputClass} required />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Option B *</label>
                                <input data-testid={`q-b-${idx}`} value={q.option_b} onChange={(e) => updateQuestion(idx, "option_b", e.target.value)} className={inputClass} required />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Option C *</label>
                                <input data-testid={`q-c-${idx}`} value={q.option_c} onChange={(e) => updateQuestion(idx, "option_c", e.target.value)} className={inputClass} required />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Option D *</label>
                                <input data-testid={`q-d-${idx}`} value={q.option_d} onChange={(e) => updateQuestion(idx, "option_d", e.target.value)} className={inputClass} required />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Correct Answer *</label>
                                <select data-testid={`q-correct-${idx}`} value={q.correct_answer} onChange={(e) => updateQuestion(idx, "correct_answer", e.target.value)} className={inputClass}>
                                  <option value="A">A</option>
                                  <option value="B">B</option>
                                  <option value="C">C</option>
                                  <option value="D">D</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Explanation (optional)</label>
                                <input data-testid={`q-explanation-${idx}`} value={q.explanation} onChange={(e) => updateQuestion(idx, "explanation", e.target.value)} className={inputClass} />
                              </div>
                            </div>
                            <button type="button" data-testid={`remove-q-${idx}`} onClick={() => removeQuestion(idx)} className="text-xs font-bold text-red-500 flex items-center gap-1 mt-1">
                              <Trash2 size={12} /> Remove Question
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <button data-testid="mt-submit" type="submit" disabled={saving} className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                  <Save size={16} /> {saving ? "Saving..." : editing ? "Update Test" : "Create Test"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm">Loading...</div>
        ) : tests.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList size={48} className="mx-auto text-slate-200 mb-3" />
            <p className="text-sm text-slate-500">No mock tests yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tests.map((test) => (
              <div key={test.id} data-testid={`mt-item-${test.id}`} className={`bg-white border p-4 flex items-center gap-3 ${test.published ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
                <div className="w-10 h-10 bg-green-50 flex items-center justify-center flex-shrink-0 rounded">
                  <ClipboardList size={18} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{test.title}</p>
                  <p className="text-[10px] text-slate-400">{test.question_count || 0} questions &middot; {test.time_limit_minutes || 30} min &middot; {test.total_marks || 100} marks</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 ${test.published ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-400"}`}>
                  {test.published ? "LIVE" : "DRAFT"}
                </span>
                <button data-testid={`mt-toggle-${test.id}`} onClick={() => togglePublish(test)} className="text-slate-400 hover:text-[#0284C7] p-1">
                  {test.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button data-testid={`mt-edit-${test.id}`} onClick={() => handleEdit(test)} className="text-slate-400 hover:text-[#0284C7] p-1">
                  <Edit2 size={16} />
                </button>
                <button data-testid={`mt-delete-${test.id}`} onClick={() => handleDelete(test.id)} className="text-slate-300 hover:text-red-500 p-1">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
